import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { clearAssistant } from '../layout/help/assistant';
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { api, configureApi, unwrap, type Schemas } from '../api/client';
import { signUpRedirect } from './invite';
import { loadSavedScheme } from '../lib/schemeSync';

export type Role = 'owner' | 'admin' | 'staff' | 'accountant' | 'read_only' | 'employee';
export interface Membership {
  id: string;
  name: string;
  role: Role;
  /** Whether one of its owners has a plan in force (ADR 0040); without one the API refuses its screens. */
  planInForce: boolean;
}
/** What kind of business it is, set when it is created (optional; migration 0018). */
export interface BusinessProfile {
  business_type?: Schemas['BusinessType'] | null;
  industry?: Schemas['Industry'] | null;
}
/** A company its owner archived (ADR 0028): hidden everywhere, and restorable. */
export interface ArchivedBusiness {
  id: string;
  name: string;
}
export interface SessionUser {
  email: string;
  name: string | null;
}
/** A practice the person is a member of (ADR 0043). */
export interface PracticeSummary {
  id: string;
  name: string;
  country: 'ZA' | 'US';
  role: 'partner' | 'manager' | 'staff';
  practiceCompanyId: string;
}
/**
 * A client company a practice member has opened (client books mode). It is not one of the person's
 * own companies: the API derives an accountant role from the practice (or read only after a
 * practice-managed company's free period).
 */
export interface ClientBooks {
  tenantId: string;
  name: string;
  practiceId: string;
  practiceName: string;
  /** The practice's link to the company, for "Back to client page". */
  linkId: string;
  role: 'accountant' | 'read_only';
}

/**
 * Supabase signs people in when it is configured. Without it, a development build (`vite dev`)
 * falls back to the API's dev-only headers, which `wrangler dev` accepts and every deployed Worker
 * refuses. A production build without Supabase settings cannot sign anyone in.
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
export const AUTH_MODE: 'supabase' | 'dev' | 'unconfigured' = SUPABASE_URL && SUPABASE_KEY ? 'supabase' : import.meta.env.DEV ? 'dev' : 'unconfigured';
const supabase: SupabaseClient | null = AUTH_MODE === 'supabase' ? createClient(SUPABASE_URL as string, SUPABASE_KEY as string) : null;

const TENANT_KEY = 'mybooks.tenant';
const CLIENT_KEY = 'mybooks.clientBooks';
const DEV_TENANTS_KEY = 'mybooks.devTenants';
export const DEV_USER = { email: 'dev@example.com', name: 'Developer' };
/** Development only: the dev user's id, found from the business's members, sent as x-dev-user. */
export const DEV_USER_KEY = 'mybooks.devUser';
/** Development only: the user to act as in one business (its dev user, else its owner), for expenses. */
export const devUserKeyFor = (tenantId: string) => `${DEV_USER_KEY}:${tenantId}`;
function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function write(key: string, value: string | null) {
  try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch { /* storage blocked */ }
}
const savedClient = (): ClientBooks | null => {
  try { return JSON.parse(read(CLIENT_KEY) ?? 'null') as ClientBooks | null; } catch { return null; }
};
const devTenants = (): Membership[] => {
  try { return (JSON.parse(read(DEV_TENANTS_KEY) ?? '[]') as Membership[]).map((t) => ({ ...t, planInForce: true })); } catch { return []; }
};

interface SessionValue {
  status: 'loading' | 'signed-out' | 'signed-in';
  user: SessionUser | null;
  tenants: Membership[];
  tenant: Membership | null;
  /** Companies this user owns that were archived, newest first. Empty in development mode. */
  archived: ArchivedBusiness[];
  /** Practices the person is a member of. */
  practices: PracticeSummary[];
  /** The client company open in client books mode, if any; `tenant` is then that company. */
  client: ClientBooks | null;
  /** Opens a client's books: the client becomes the current company until another is chosen. */
  openClient: (client: ClientBooks) => void;
  selectTenant: (id: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<'signed-in' | 'confirm-email'>;
  signOut: () => Promise<void>;
  /** Creates a business with the caller as owner and switches to it. */
  createBusiness: (name: string, currency: string, profile?: BusinessProfile) => Promise<Membership>;
  /** Archives the current business (owners only), then switches to another or to the first-business screen. */
  archiveBusiness: () => Promise<void>;
  /** Brings an archived business back and switches to it. */
  restoreBusiness: (id: string) => Promise<void>;
  /** Development only: use a business that already exists in the local database. */
  addDevTenant: (id: string, name: string) => void;
  refresh: () => Promise<void>;
}
const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SessionValue['status']>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [tenants, setTenants] = useState<Membership[]>([]);
  const [archived, setArchived] = useState<ArchivedBusiness[]>([]);
  const [practices, setPractices] = useState<PracticeSummary[]>([]);
  const [client, setClient] = useState<ClientBooks | null>(savedClient);
  const [tenantId, setTenantId] = useState<string | null>(read(TENANT_KEY));
  const token = useRef<string | null>(null);
  const tenantRef = useRef<string | null>(tenantId);
  tenantRef.current = tenantId;

  const loadMemberships = useCallback(async () => {
    if (AUTH_MODE === 'dev') {
      setTenants(devTenants());
      // Practice routes act as a user: in development that is x-dev-user, once a business has found it.
      if (read(DEV_USER_KEY)) {
        const res = await api.GET('/v1/practices').catch(() => null);
        setPractices(((res?.data?.data ?? []) as Schemas['PracticeSummary'][]).map(practiceOf));
      }
      return;
    }
    const me = await unwrap(api.GET('/v1/me'));
    const list: Membership[] = (me.data?.tenants ?? []).map((t) => ({ id: t.id as string, name: t.name as string, role: t.role as Role, planInForce: t.plan_in_force === true }));
    setTenants(list);
    setArchived(((me.data as { archived?: ArchivedBusiness[] } | undefined)?.archived ?? []).map(({ id, name }) => ({ id, name })));
    setPractices((me.data?.practices ?? []).map(practiceOf));
    if (me.data?.user) setUser({ email: String((me.data.user as { email?: string }).email ?? ''), name: ((me.data.user as { name?: string | null }).name) ?? null });
  }, []);

  const signedOut = useCallback(() => {
    token.current = null;
    setUser(null);
    setTenants([]);
    setArchived([]);
    setPractices([]);
    setStatus('signed-out');
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    configureApi(
      async () => {
        const headers: Record<string, string> = {};
        if (AUTH_MODE === 'dev') {
          if (tenantRef.current) headers['x-dev-tenant'] = tenantRef.current;
          // Group routes act as a user; in development that is the dev user's id (pages/groups/data.ts).
          // Expenses and trips belong to a person too: in a business, act as its own dev user.
          const devUser = (tenantRef.current ? read(devUserKeyFor(tenantRef.current)) : null) ?? read(DEV_USER_KEY);
          if (devUser) headers['x-dev-user'] = devUser;
          return headers;
        }
        if (token.current) headers.authorization = `Bearer ${token.current}`;
        if (tenantRef.current) headers['x-tenant-id'] = tenantRef.current;
        return headers;
      },
      () => { if (AUTH_MODE === 'supabase') void supabase?.auth.signOut(); },
    );
    if (AUTH_MODE === 'dev') {
      setUser(DEV_USER);
      setTenants(devTenants());
      // The practices load before the app decides where to land.
      loadMemberships().finally(() => setStatus('signed-in'));
      return;
    }
    if (!supabase) { setStatus('signed-out'); return; }
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) { signedOut(); return; }
      const changed = token.current === null;
      token.current = session.access_token;
      if (changed) {
        setUser({ email: session.user.email ?? '', name: (session.user.user_metadata?.full_name as string | undefined) ?? null });
        loadMemberships().then(() => setStatus('signed-in'), () => signedOut());
        // The color scheme saved to the account replaces this browser's copy (lib/schemeSync.ts).
        void loadSavedScheme();
      }
    });
    return () => data.subscription.unsubscribe();
  }, [loadMemberships, signedOut]);

  // In client books mode, the client's company. Otherwise the business used last, else one that can
  // be used, else the first.
  const clientMembership = useMemo<Membership | null>(
    () => (client && client.tenantId === tenantId ? { id: client.tenantId, name: client.name, role: client.role, planInForce: true } : null),
    [client, tenantId],
  );
  const tenant = clientMembership ?? tenants.find((t) => t.id === tenantId) ?? tenants.find((t) => t.planInForce) ?? tenants[0] ?? null;
  useEffect(() => {
    if (tenant && tenant.id !== tenantId) { setTenantId(tenant.id); write(TENANT_KEY, tenant.id); }
  }, [tenant, tenantId]);

  const selectTenant = useCallback((id: string) => {
    tenantRef.current = id;
    setTenantId(id);
    write(TENANT_KEY, id);
    setClient(null);
    write(CLIENT_KEY, null);
  }, []);
  const openClient = useCallback((next: ClientBooks) => {
    tenantRef.current = next.tenantId;
    setClient(next);
    write(CLIENT_KEY, JSON.stringify(next));
    setTenantId(next.tenantId);
    write(TENANT_KEY, next.tenantId);
  }, []);

  const value = useMemo<SessionValue>(() => ({
    status, user, tenants, tenant, archived, practices, client: clientMembership ? client : null,
    selectTenant, openClient,
    async signIn(email, password) {
      if (!supabase) throw new Error('Sign-in is not configured');
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    async signUp(email, password, name) {
      if (!supabase) throw new Error('Sign-in is not configured');
      const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: name }, emailRedirectTo: signUpRedirect() } });
      if (error) throw error;
      return data.session ? 'signed-in' : 'confirm-email';
    },
    async signOut() {
      // The assistant's conversation starts fresh after sign-out (APP-DESIGN.md 11.2).
      clearAssistant();
      if (supabase) await supabase.auth.signOut();
      write(TENANT_KEY, null);
      write(CLIENT_KEY, null);
      setClient(null);
      if (AUTH_MODE === 'dev') { queryClient.clear(); return; }
      signedOut();
    },
    async createBusiness(name, currency, profile = {}) {
      // A signed-in caller always owns what they create: the API takes the owner from the token. Only
      // the dev path, which has no token, names the owner in the body. (The spec marks owner_email as
      // required because it documents the dev path.)
      const base = { name, base_currency: currency, ...profile };
      const body = AUTH_MODE === 'dev' ? { ...base, owner_email: DEV_USER.email, owner_name: DEV_USER.name } : base;
      const created = await unwrap(api.POST('/v1/tenants', { body: body as { name: string; owner_email: string } }));
      // Creating a business needs a plan in force (ADR 0040), so the new one can be used.
      const membership: Membership = { id: created.data?.id as string, name, role: 'owner', planInForce: true };
      if (AUTH_MODE === 'dev') {
        const list = [...devTenants(), membership];
        write(DEV_TENANTS_KEY, JSON.stringify(list));
        setTenants(list);
      } else {
        await loadMemberships();
      }
      selectTenant(membership.id);
      return membership;
    },
    async archiveBusiness() {
      const current = tenantRef.current;
      if (!current) return;
      await unwrap(api.POST('/v1/tenant/archive'));
      queryClient.clear();
      if (AUTH_MODE === 'dev') {
        const list = devTenants().filter((t) => t.id !== current);
        write(DEV_TENANTS_KEY, JSON.stringify(list));
        setTenants(list);
      } else {
        await loadMemberships();
      }
      // The old id is gone from the list, so the effect above picks the first remaining business.
      write(TENANT_KEY, null);
      setTenantId(null);
    },
    async restoreBusiness(id) {
      await unwrap(api.POST('/v1/tenants/{id}/restore', { params: { path: { id } } }));
      await loadMemberships();
      queryClient.clear();
      selectTenant(id);
    },
    addDevTenant(id, name) {
      // The dev headers skip the plan check, as they skip signing in.
      const list = [...devTenants().filter((t) => t.id !== id), { id, name, role: 'owner' as const, planInForce: true }];
      write(DEV_TENANTS_KEY, JSON.stringify(list));
      setTenants(list);
      selectTenant(id);
    },
    refresh: loadMemberships,
  }), [status, user, tenants, tenant, archived, practices, client, clientMembership, selectTenant, openClient, signedOut, loadMemberships, queryClient]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

const practiceOf = (p: Schemas['PracticeSummary']): PracticeSummary => ({
  id: p.id as string,
  name: p.name as string,
  country: (p.country ?? 'ZA') as PracticeSummary['country'],
  role: (p.role ?? 'staff') as PracticeSummary['role'],
  practiceCompanyId: p.practice_company_id as string,
});

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}

/** The signed-in business. Only call under the app shell, which renders nothing until one is chosen. */
export function useTenant(): Membership {
  const { tenant } = useSession();
  if (!tenant) throw new Error('useTenant without a business');
  return tenant;
}

export const canWrite = (role: Role) => role !== 'read_only' && role !== 'employee';
export const canAdmin = (role: Role) => role === 'owner' || role === 'admin';
/** An employee sees only their own expenses (ADR 0023); the API and database refuse them the books. */
export const isEmployee = (role: Role) => role === 'employee';
export const canClaimExpenses = (role: Role) => role !== 'read_only';
export const canApproveExpenses = (role: Role) => role === 'owner' || role === 'admin' || role === 'accountant';
export const ROLE_NAMES: Record<Role, string> = { owner: 'Owner', admin: 'Admin', staff: 'Staff', accountant: 'Accountant', read_only: 'Read only', employee: 'Employee' };
